import { useCallback, useLayoutEffect, useRef } from 'react';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';
import {
    buildUnavailableResources,
    createRiskOverviewSnapshotPublicationTransaction,
} from '../riskOverviewResource';
import { resolveSelectedOverviewScope } from '../selectedOverviewScope';

const MAX_SNAPSHOT_ATTEMPTS = 3;

const buildVisibleSelectionKey = (userUid, portfolioId) => JSON.stringify([
    String(userUid ?? ''),
    String(portfolioId ?? ''),
]);

// The portfolio selected in Risk Overview is refreshed by one explicit command,
// refreshSelectedOverview({ reason }). Code that changes something the
// selected Overview depends on calls it once, after making the change: the
// selection handler, opening Overview, each risk workflow when it finishes,
// and a saved-portfolio list refresh that changes the selected portfolio.
//
// The command reads the latest inputs through readSelectedOverviewInputs, so a
// call made right after a state update does not depend on React having
// re-rendered. It requests nothing while Overview is hidden (opening Overview
// calls it again) or while another owner holds the selected Overview: a core
// load that includes it, a Position Sizing sync, or a portfolio import. Those
// owners call the command when they finish.
export default function useOverviewSelectionRefresh({
    activeView,
    userUid,
    selectedSummaryPortfolioMeta,
    readSelectedOverviewInputs,
    setOptimalPortfolioSummary,
    setOptimalPortfolioSummaryId,
    setOptimalPortfolioSummaryLoaded,
    setOptimizedRiskMetrics,
    setOptimizedGroupRiskMetrics,
    riskCommandsRef,
    lastSeenOptimizedOverviewRefreshRef,
    selectedOverviewRefreshRequestIdRef,
    coreSelectedOverviewOwnerRef,
    posSizingSyncInFlightRef,
    importApplyInFlightRef,
    exclusiveCoreLoadOwnerRef = null,
    onIncompatiblePortfolioSelected = null,
}) {
    // Workflows keep the command they looked up when they started, so the
    // command reads the view and callbacks of the latest render.
    const latestRef = useRef(null);
    latestRef.current = {
        activeView,
        readSelectedOverviewInputs,
        onIncompatiblePortfolioSelected,
    };

    const visibleSelectionKey = buildVisibleSelectionKey(
        userUid,
        selectedSummaryPortfolioMeta?.id,
    );
    const visibleSelectionKeyRef = useRef(visibleSelectionKey);
    const fenceVisibleSelection = useCallback((nextSelectionKey) => {
        if (visibleSelectionKeyRef.current === nextSelectionKey) return;
        visibleSelectionKeyRef.current = nextSelectionKey;
        selectedOverviewRefreshRequestIdRef.current += 1;
        setOptimalPortfolioSummaryLoaded(false);
    }, [
        selectedOverviewRefreshRequestIdRef,
        setOptimalPortfolioSummaryLoaded,
    ]);

    // A user/session switch, or a selection cleared without the handler (the
    // Watchlist restoration prompt, a deleted portfolio), must fence an
    // in-flight response before paint so it cannot publish for the new state.
    useLayoutEffect(() => {
        fenceVisibleSelection(visibleSelectionKey);
    }, [fenceVisibleSelection, visibleSelectionKey]);

    const refreshSelectedOverview = useCallback(({ reason = 'unspecified' } = {}) => {
        if (exclusiveCoreLoadOwnerRef?.current) return;
        const latest = latestRef.current;
        const inputs = latest.readSelectedOverviewInputs();
        const scope = resolveSelectedOverviewScope(inputs);
        if (!scope) {
            lastSeenOptimizedOverviewRefreshRef.current = {
                id: null,
                token: null,
                contextKey: '',
                inFlightKey: '',
                userUid: inputs.userUid ?? null,
            };
            setOptimalPortfolioSummary(null);
            setOptimalPortfolioSummaryId(null);
            setOptimalPortfolioSummaryLoaded(true);
            return;
        }
        const showingOverview = latest.activeView === 'overview';
        if (
            showingOverview
            && latest.onIncompatiblePortfolioSelected?.(scope.portfolio) === true
        ) {
            lastSeenOptimizedOverviewRefreshRef.current = {
                id: null,
                token: scope.refreshToken,
                contextKey: '',
                inFlightKey: '',
                userUid: inputs.userUid,
            };
            setOptimalPortfolioSummaryLoaded(true);
            return;
        }
        if (
            !showingOverview
            || coreSelectedOverviewOwnerRef.current != null
            || posSizingSyncInFlightRef.current
            || importApplyInFlightRef.current
        ) {
            return;
        }

        const previous = lastSeenOptimizedOverviewRefreshRef.current;
        if (
            previous.inFlightKey === scope.scopeKey
            && previous.inFlightRequestId === selectedOverviewRefreshRequestIdRef.current
        ) {
            return;
        }
        const selectionChanged = previous.id !== scope.portfolioId;
        const tokenChanged = previous.token !== scope.refreshToken;
        const contextChanged = Boolean(
            previous.contextKey && previous.contextKey !== scope.contextKey,
        );
        const userChanged = previous.userUid != null && previous.userUid !== inputs.userUid;
        if (!selectionChanged && !tokenChanged && !contextChanged && !userChanged) {
            return;
        }

        const requestId = ++selectedOverviewRefreshRequestIdRef.current;
        lastSeenOptimizedOverviewRefreshRef.current = {
            ...previous,
            inFlightKey: scope.scopeKey,
            inFlightRequestId: requestId,
        };
        const nextSeen = {
            id: scope.portfolioId,
            token: scope.refreshToken,
            contextKey: scope.contextKey,
            inFlightKey: '',
            userUid: inputs.userUid,
        };
        const ownsSelectedOverviewLoading = () => (
            selectedOverviewRefreshRequestIdRef.current === requestId
        );
        const ownsRefreshEntry = () => {
            const entry = lastSeenOptimizedOverviewRefreshRef.current;
            return entry?.inFlightKey === scope.scopeKey && entry?.inFlightRequestId === requestId;
        };
        const isCurrentSelectedRequest = () => (
            !exclusiveCoreLoadOwnerRef?.current && ownsSelectedOverviewLoading() && ownsRefreshEntry()
        );
        setOptimalPortfolioSummaryLoaded(false);
        void (async () => {
            let refreshSucceeded = false;
            let refreshSettled = false;
            let selectedPublication = null;
            try {
                const commands = getRiskCommandsOrThrow(
                    riskCommandsRef,
                    'useOverviewSelectionRefresh.refreshSelectedOverview',
                    ['refreshPortfolioRisk'],
                );
                for (let attempt = 0; attempt < MAX_SNAPSHOT_ATTEMPTS; attempt += 1) {
                    if (!isCurrentSelectedRequest()) return;
                    selectedPublication = createRiskOverviewSnapshotPublicationTransaction({
                        requiredPanels: ['primary', 'group'],
                        scopeKey: scope.scopeKey,
                        generation: requestId,
                        isCurrent: isCurrentSelectedRequest,
                    });
                    // Saved Position Sizing selections resolve against their own
                    // frozen context; Actual-context drift is status-only here.
                    await commands.refreshPortfolioRisk({
                        portfolioId: scope.portfolioId,
                        targetKey: scope.targetKey,
                        riskContextOverride: scope.riskContext,
                        isCurrentRequest: isCurrentSelectedRequest,
                        stagePublicationState: selectedPublication.stage,
                        generation: requestId,
                        resourceScopeKey: scope.scopeKey,
                    });
                    // Retry both panels only when complete, current responses
                    // disagree. Missing or superseded publications stay fenced.
                    const snapshotMismatch = selectedPublication.hasSnapshotMismatch();
                    if (selectedPublication.commit()) {
                        refreshSucceeded = true;
                        refreshSettled = true;
                        return;
                    }
                    if (!snapshotMismatch || !isCurrentSelectedRequest()) return;
                }
                if (isCurrentSelectedRequest()) {
                    const unavailable = buildUnavailableResources({
                        scopeKey: scope.scopeKey,
                        generation: requestId,
                        isSelected: true,
                    });
                    setOptimizedRiskMetrics(unavailable.primary);
                    setOptimizedGroupRiskMetrics(unavailable.group);
                    refreshSettled = true;
                }
            } catch {
                console.error('Error refreshing selected Overview risk.', reason);
            } finally {
                if (!refreshSucceeded) {
                    selectedPublication?.discard();
                }
                if (ownsRefreshEntry()) {
                    lastSeenOptimizedOverviewRefreshRef.current = refreshSucceeded
                        ? nextSeen
                        : {
                            ...lastSeenOptimizedOverviewRefreshRef.current,
                            inFlightKey: '',
                            inFlightRequestId: null,
                        };
                }
                if (refreshSettled && !exclusiveCoreLoadOwnerRef?.current && ownsSelectedOverviewLoading()) {
                    setOptimalPortfolioSummaryLoaded(true);
                }
            }
        })();
    }, [
        exclusiveCoreLoadOwnerRef,
        coreSelectedOverviewOwnerRef,
        importApplyInFlightRef,
        lastSeenOptimizedOverviewRefreshRef,
        posSizingSyncInFlightRef,
        riskCommandsRef,
        selectedOverviewRefreshRequestIdRef,
        setOptimalPortfolioSummary,
        setOptimalPortfolioSummaryId,
        setOptimalPortfolioSummaryLoaded,
        setOptimizedRiskMetrics,
        setOptimizedGroupRiskMetrics,
    ]);

    const handleWhatIfPortfolioChangeSummary = useCallback((e) => {
        if (exclusiveCoreLoadOwnerRef?.current) return;
        const [id, name] = e.target.value;
        fenceVisibleSelection(buildVisibleSelectionKey(userUid, id));
        setOptimalPortfolioSummary(name);
        setOptimalPortfolioSummaryId(id);
        refreshSelectedOverview({ reason: 'portfolio_selected' });
    }, [
        exclusiveCoreLoadOwnerRef,
        fenceVisibleSelection,
        refreshSelectedOverview,
        setOptimalPortfolioSummary,
        setOptimalPortfolioSummaryId,
        userUid,
    ]);

    return {
        handleWhatIfPortfolioChangeSummary,
        refreshSelectedOverview,
    };
}
